import React from 'react';
import {
    Grid, Stack, Card, CardContent, Typography, FormControl,
    InputLabel, Select, MenuItem, TextField, Button, Box,
    List, ListItem, ListItemText, Divider, Chip, Paper,
    IconButton, Fade, useTheme
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import LocalDrinkRoundedIcon from '@mui/icons-material/LocalDrinkRounded';
import EuroRoundedIcon from '@mui/icons-material/EuroRounded';

const TreasuryFinesTab = ({
    isKassenwart, fineForm, setFineForm, handleAssignFine, members, catalog,
    showCatalogForm, setShowCatalogForm, handleCreateCatalog, catalogForm,
    setCatalogForm, aggregatedFines, fines, handlePayFine
}) => {
    const theme = useTheme();

    return (
        <Grid container spacing={3}>
            {/* Left Column: Management */}
            <Grid>
                {isKassenwart && (
                    <Stack spacing={3}>
                        {/* Assign Fine Card */}
                        <Paper
                            sx={{
                                p: 3,
                                borderRadius: "12px",
                                border: '1px solid',
                                borderColor: 'primary.main',
                                background: 'rgba(190, 242, 100, 0.03)'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HistoryRoundedIcon color="primary" /> Strafe verhängen
                            </Typography>
                            <form onSubmit={handleAssignFine}>
                                <Stack spacing={2.5}>
                                    <FormControl fullWidth>
                                        <InputLabel>Spieler</InputLabel>
                                        <Select
                                            value={fineForm.user_id}
                                            label="Spieler"
                                            onChange={e => setFineForm({ ...fineForm, user_id: e.target.value })}
                                        >
                                            {members.map(m => (
                                                <MenuItem key={m.id} value={m.id}>
                                                    {m.first_name} {m.last_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <InputLabel>Vergehen (Katalog)</InputLabel>
                                        <Select
                                            value={fineForm.katalog_id}
                                            label="Vergehen (Katalog)"
                                            onChange={e => setFineForm({ ...fineForm, katalog_id: e.target.value })}
                                        >
                                            {catalog.map(c => (
                                                <MenuItem key={c.id} value={c.id}>
                                                    {c.grund} ({c.typ === 'GELD' ? `${c.betrag}€` : c.typ === 'KISTEN' ? `${parseInt(c.betrag)} Kisten` : 'Individuell'})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        type="date"
                                        label="Datum"
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        fullWidth
                                        value={fineForm.datum}
                                        onChange={e => setFineForm({ ...fineForm, datum: e.target.value })}
                                    />
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        sx={{ py: 1.5, fontWeight: 900, bgcolor: 'primary.main', color: '#000' }}
                                    >
                                        Strafe eintragen
                                    </Button>
                                </Stack>
                            </form>
                        </Paper>

                        {/* Catalog Management */}
                        <Paper sx={{ p: 0, borderRadius: "12px", border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>Strafenkatalog</Typography>
                                {!showCatalogForm && (
                                    <Button
                                        size="small"
                                        startIcon={<AddRoundedIcon />}
                                        onClick={() => setShowCatalogForm(true)}
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Neu
                                    </Button>
                                )}
                            </Box>

                            {showCatalogForm && (
                                <Fade in>
                                    <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.02)', borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <form onSubmit={handleCreateCatalog}>
                                            <Stack spacing={2}>
                                                <TextField
                                                    label="Vergehen Name"
                                                    required
                                                    fullWidth
                                                    value={catalogForm.grund}
                                                    onChange={e => setCatalogForm({ ...catalogForm, grund: e.target.value })}
                                                />
                                                <FormControl fullWidth>
                                                    <InputLabel>Typ</InputLabel>
                                                    <Select
                                                        value={catalogForm.typ}
                                                        label="Typ"
                                                        onChange={e => setCatalogForm({ ...catalogForm, typ: e.target.value })}
                                                    >
                                                        <MenuItem value="GELD">Geldstrafe (€)</MenuItem>
                                                        <MenuItem value="KISTEN">Bierkisten</MenuItem>
                                                        <MenuItem value="INDIVIDUELL">Individuell (Text)</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                {catalogForm.typ !== 'INDIVIDUELL' ? (
                                                    <TextField
                                                        label={catalogForm.typ === 'GELD' ? "Betrag in €" : "Anzahl Kisten"}
                                                        type="number"
                                                        required
                                                        fullWidth
                                                        value={catalogForm.betrag}
                                                        onChange={e => setCatalogForm({ ...catalogForm, betrag: e.target.value })}
                                                    />
                                                ) : (
                                                    <TextField
                                                        label="Beschreibung (z.B. Leibchen waschen)"
                                                        required
                                                        fullWidth
                                                        value={catalogForm.individueller_text}
                                                        onChange={e => setCatalogForm({ ...catalogForm, individueller_text: e.target.value })}
                                                    />
                                                )}
                                                <Stack direction="row" spacing={1}>
                                                    <Button variant="contained" type="submit" sx={{ fontWeight: 900, px: 3 }}>Hinzufügen</Button>
                                                    <Button onClick={() => setShowCatalogForm(false)} sx={{ fontWeight: 800 }}>Abbrechen</Button>
                                                </Stack>
                                            </Stack>
                                        </form>
                                    </Box>
                                </Fade>
                            )}

                            <List sx={{ p: 0 }}>
                                {catalog.map((c, idx) => (
                                    <ListItem
                                        key={c.id}
                                        sx={{
                                            px: 3,
                                            py: 2,
                                            borderBottom: idx === catalog.length - 1 ? 'none' : '1px solid',
                                            borderColor: 'divider',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }
                                        }}
                                    >
                                        <ListItemText
                                            primary={c.grund}
                                            primaryTypographyProps={{ fontWeight: 700 }}
                                            secondary={c.typ === 'GELD' ? `${c.betrag} €` : c.typ === 'KISTEN' ? `${parseInt(c.betrag)} Kisten` : c.individueller_text}
                                            secondaryTypographyProps={{ fontWeight: 600, color: 'primary.main' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Stack>
                )}
            </Grid>

            {/* Right Column: Open Fines */}
            <Grid sx={{ width: "100%" }}>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Offene Strafen</Typography>
                    <Chip
                        label={Object.keys(aggregatedFines).length}
                        size="small"
                        sx={{ fontWeight: 900, bgcolor: 'primary.main', color: '#000' }}
                    />
                </Box>

                <Stack spacing={2}>
                    {Object.entries(aggregatedFines).map(([userId, data]) => (
                        <Fade in key={userId}>
                            <Paper
                                sx={{
                                    p: 3,
                                    borderRadius: "12px",
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    background: 'rgba(255,255,255,0.02)',
                                    transition: 'all 0.3s',
                                    '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(190, 242, 100, 0.02)' }
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{data.name}</Typography>
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                            {data.geld > 0 && (
                                                <Chip
                                                    icon={<EuroRoundedIcon sx={{ fontSize: '14px !important' }} />}
                                                    label={`${data.geld.toFixed(2)} €`}
                                                    size="small"
                                                    sx={{ fontWeight: 800, bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' }}
                                                />
                                            )}
                                            {data.kisten > 0 && (
                                                <Chip
                                                    icon={<LocalDrinkRoundedIcon sx={{ fontSize: '14px !important' }} />}
                                                    label={`${data.kisten} Kisten`}
                                                    size="small"
                                                    sx={{ fontWeight: 800, bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.2)' }}
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>

                                <Stack spacing={1.5} sx={{ mt: 3 }}>
                                    {fines.filter(f => f.user_id.toString() === userId && !f.ist_bezahlt).map(f => (
                                        <Box
                                            key={f.id}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                p: 2,
                                                borderRadius: "12px",
                                                bgcolor: 'rgba(255,255,255,0.02)',
                                                border: '1px solid transparent',
                                                transition: '0.2s',
                                                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', borderColor: 'divider' }
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {f.grund} {f.typ === 'INDIVIDUELL' && `(${f.individueller_text})`}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                    {new Date(f.datum).toLocaleDateString('de-DE')}
                                                </Typography>
                                            </Box>
                                            {isKassenwart && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<CheckCircleRoundedIcon />}
                                                    onClick={() => handlePayFine(f.id)}
                                                    sx={{
                                                        borderRadius: "12px",
                                                        fontWeight: 800,
                                                        fontSize: '0.7rem',
                                                        color: 'primary.main',
                                                        borderColor: 'rgba(190, 242, 100, 0.3)',
                                                        '&:hover': { bgcolor: 'primary.main', color: '#000', borderColor: 'primary.main' }
                                                    }}
                                                >
                                                    Bezahlt
                                                </Button>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        </Fade>
                    ))}

                    {Object.keys(aggregatedFines).length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 10, borderRadius: "12px", border: '1px dashed', borderColor: 'divider' }}>
                            <InfoRoundedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Keine offenen Strafen vorhanden.
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                Alles im grünen Bereich!
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Grid>
        </Grid>
    );
};

export default TreasuryFinesTab;
